// Preset management (D10): import/export JSON + localStorage. All imported JSON
// goes through the engine's validating deserialize (clamp ranges, drop unknown
// keys, no eval). localStorage is best-effort — wrapped so a sandboxed/blocked
// store fails silently rather than crashing.

import { AudioEngine, serialize, deserialize, getDefaultPreset } from '../engine';

const STORAGE_KEY = 'claudesynth.preset.v1';

function flash(status: HTMLElement | null, msg: string): void {
  if (!status) return;
  status.textContent = msg;
  window.setTimeout(() => {
    if (status.textContent === msg) status.textContent = '';
  }, 2500);
}

function safeLocalStorage(): Storage | null {
  try {
    const s = window.localStorage;
    const k = '__cs_probe__';
    s.setItem(k, '1');
    s.removeItem(k);
    return s;
  } catch {
    return null;
  }
}

export function initPresetBar(engine: AudioEngine, root: ParentNode): void {
  const bar = root.querySelector<HTMLElement>('#preset-bar');
  if (!bar) return;
  const status = bar.querySelector<HTMLElement>('#preset-status');
  const fileInput = bar.querySelector<HTMLInputElement>('#preset-file');
  const store = safeLocalStorage();

  bar.querySelector('[data-preset="save"]')?.addEventListener('click', () => {
    if (!store) return flash(status, 'Save unavailable here');
    try {
      store.setItem(STORAGE_KEY, serialize(engine.getPreset('Saved')));
      flash(status, 'Saved');
    } catch {
      flash(status, 'Save failed');
    }
  });

  bar.querySelector('[data-preset="load"]')?.addEventListener('click', () => {
    const json = store?.getItem(STORAGE_KEY);
    if (!json) return flash(status, 'No saved patch');
    try {
      engine.loadPreset(deserialize(json));
      flash(status, 'Loaded');
    } catch {
      flash(status, 'Load failed');
    }
  });

  bar.querySelector('[data-preset="export"]')?.addEventListener('click', () => {
    const json = serialize(engine.getPreset('Export'));
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'claudesynth-patch.json';
    a.click();
    URL.revokeObjectURL(url);
    flash(status, 'Exported');
  });

  bar.querySelector('[data-preset="import"]')?.addEventListener('click', () => {
    fileInput?.click();
  });

  fileInput?.addEventListener('change', async () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      engine.loadPreset(deserialize(text)); // validated/clamped
      flash(status, 'Imported');
    } catch {
      flash(status, 'Invalid file');
    } finally {
      fileInput.value = '';
    }
  });

  bar.querySelector('[data-preset="init"]')?.addEventListener('click', () => {
    engine.loadPreset(getDefaultPreset());
    flash(status, 'Init patch');
  });

  bar.querySelector('#panic-btn')?.addEventListener('click', () => {
    engine.panic();
    flash(status, 'Panic!');
  });
}
